import { QueryClient } from '@tanstack/react-query';

/**
 * Global QueryClient configuration
 *
 * Configured with sensible defaults for the ChecksAndTicks application:
 * - 5 minute stale time (data is considered fresh for 5 minutes)
 * - 10 minute cache time (data is kept in cache for 10 minutes)
 * - Retry failed queries 1 time
 * - Don't refetch on window focus in development (less noisy)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 5 minutes
      staleTime: 1000 * 60 * 5, // 5 minutes

      // Data is kept in cache for 10 minutes after becoming unused
      gcTime: 1000 * 60 * 10, // 10 minutes (previously cacheTime)

      // Retry failed queries once (not too aggressive)
      retry: 1,

      // Retry delay increases exponentially (0ms, 1000ms, 2000ms)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch on window focus (production) but not in development
      refetchOnWindowFocus: import.meta.env.PROD,

      // Refetch on reconnect
      refetchOnReconnect: true,

      // Refetch on mount only if data is stale
      refetchOnMount: true,
    },
    mutations: {
      // Do not retry failed mutations — side-effects must not fire twice
      retry: 0,

      // Retry delay for mutations
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

/**
 * Query keys factory for consistent query key generation
 *
 * Usage:
 * ```ts
 * const { data } = useQuery({
 *   queryKey: queryKeys.assets.list({ limit: 10 }),
 *   queryFn: () => apiService.getAssets({ limit: 10 })
 * });
 * ```
 */
export const queryKeys = {
  // Authentication
  auth: {
    user: () => ['auth', 'user'] as const,
    tenant: () => ['auth', 'tenant'] as const,
  },

  // Sites
  sites: {
    all: () => ['sites'] as const,
    lists: () => ['sites', 'list'] as const,
    list: (params?: any) => ['sites', 'list', params] as const,
    detail: (id: string) => ['sites', 'detail', id] as const,
    users: (siteId: string) => ['sites', siteId, 'users'] as const,
    summary: (siteId: string) => ['sites', siteId, 'summary'] as const,
  },

  // Assets
  assets: {
    all: () => ['assets'] as const,
    lists: () => ['assets', 'list'] as const,
    list: (params?: any) => ['assets', 'list', params] as const,
    details: () => ['assets', 'detail'] as const,
    detail: (id: string) => ['assets', 'detail', id] as const,
    types: () => ['assets', 'types'] as const,
    forms: (assetId: string) => ['assets', assetId, 'forms'] as const,
  },

  // Forms
  forms: {
    all: () => ['forms'] as const,
    lists: () => ['forms', 'list'] as const,
    list: (params?: any) => ['forms', 'list', params] as const,
    details: () => ['forms', 'detail'] as const,
    detail: (id: string) => ['forms', 'detail', id] as const,
  },

  // Form Templates
  templates: {
    all: () => ['templates'] as const,
    lists: () => ['templates', 'list'] as const,
    list: (params?: any) => ['templates', 'list', params] as const,
    details: () => ['templates', 'detail'] as const,
    detail: (id: string) => ['templates', 'detail', id] as const,
    library: (params?: any) => ['templates', 'library', params] as const,
  },

  // Submissions
  submissions: {
    all: () => ['submissions'] as const,
    lists: () => ['submissions', 'list'] as const,
    list: (params?: any) => ['submissions', 'list', params] as const,
    details: () => ['submissions', 'detail'] as const,
    detail: (id: string) => ['submissions', 'detail', id] as const,
  },

  // Users
  users: {
    all: () => ['users'] as const,
    lists: () => ['users', 'list'] as const,
    list: (params?: any) => ['users', 'list', params] as const,
    details: () => ['users', 'detail'] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
  },

  // Dashboard
  dashboard: {
    all: () => ['dashboard'] as const,
    stats: () => ['dashboard', 'stats'] as const,
  },

  // Analytics
  analytics: {
    all: () => ['analytics'] as const,
    dashboard: (params?: any) => ['analytics', 'dashboard', params] as const,
    trends: (params?: any) => ['analytics', 'trends', params] as const,
    performance: (params?: any) => ['analytics', 'performance', params] as const,
  },

  // Maintenance
  maintenance: {
    schedules: {
      all: () => ['maintenance', 'schedules'] as const,
      list: (params?: any) => ['maintenance', 'schedules', 'list', params] as const,
      detail: (id: string) => ['maintenance', 'schedules', 'detail', id] as const,
    },
    tasks: {
      all: () => ['maintenance', 'tasks'] as const,
      list: (params?: any) => ['maintenance', 'tasks', 'list', params] as const,
      upcoming: (days?: number) => ['maintenance', 'tasks', 'upcoming', days] as const,
      overdue: () => ['maintenance', 'tasks', 'overdue'] as const,
    },
  },

  // Notifications
  notifications: {
    all: () => ['notifications'] as const,
    unread: () => ['notifications', 'unread'] as const,
    count: () => ['notifications', 'count'] as const,
  },

  // Admin
  admin: {
    tenants: () => ['admin', 'tenants'] as const,
    errors: (params?: any) => ['admin', 'errors', params] as const,
    auditLogs: (params?: any) => ['admin', 'audit-logs', params] as const,
  },
} as const;
